// See cart-drawer.js — window, not a relative import, so cache-busting works.
const lockScroll = (holder) => window.ThemeScrollLock?.lockScroll(holder);
const unlockScroll = (holder) => window.ThemeScrollLock?.unlockScroll(holder);
const trapFocus = (container, initial) => window.ThemeScrollLock?.trapFocus(container, initial);

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('[data-site-header]');
    this.menuPanel = this.querySelector('[data-mobile-menu]');
    this.menuBackdrop = this.querySelector('[data-mobile-menu-backdrop]');
    this.releaseFocusTrap = null;

    this.querySelectorAll('[data-menu-toggle]').forEach((el) =>
      el.addEventListener('click', () => this.toggleMenu())
    );
    this.querySelectorAll('[data-menu-close]').forEach((el) =>
      el.addEventListener('click', () => this.closeMenu())
    );
    this.menuBackdrop?.addEventListener('click', () => this.closeMenu());

    this.querySelectorAll('[data-cart-toggle]').forEach((el) =>
      el.addEventListener('click', (event) => {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('cart:open'));
      })
    );
    this.querySelectorAll('[data-search-toggle]').forEach((el) =>
      el.addEventListener('click', (event) => {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('search:open'));
      })
    );

    // Listeners on `document` outlive this element, so re-connecting — which the
    // theme editor does on every header edit — would stack a duplicate each
    // time. Bind them once; the child bindings above are re-run because those
    // nodes really are replaced.
    if (!this.hasSetUp) {
      this.hasSetUp = true;

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isMenuOpen()) this.closeMenu();
      });

      // Opening the cart or search from inside the mobile menu used to leave the
      // menu open behind them, so the header kept holding the scroll lock. The
      // visitor then closed the drawer and the page stayed frozen with nothing
      // visible to close.
      ['cart:open', 'search:open'].forEach((name) =>
        document.addEventListener(name, () => {
          if (this.isMenuOpen()) this.closeMenu();
        })
      );

      document.addEventListener('cart:updated', (event) => {
        const count = Number(event.detail?.count ?? 0);
        this.querySelectorAll('[data-cart-item-count]').forEach((el) => {
          el.textContent = count;
          el.toggleAttribute('hidden', count === 0);
        });
      });
    }

    this.setupSubmenus();
    this.setupSticky();
  }

  // Submenu disclosures, used by both the desktop nav and the mobile drawer.
  // The parent's link is left alone — it navigates. Only the chevron toggles.
  //
  // Bound per connect rather than once, because these live in the header's own
  // markup and are replaced when the section re-renders. The document-level
  // listeners that must not stack are registered inside the hasSetUp block.
  setupSubmenus() {
    this.navParents = Array.from(this.querySelectorAll('[data-nav-parent]'));

    // Hover only where hovering is real. On a touch screen `(hover: hover)` is
    // false, so the chevron stays the way in — otherwise the panel would open on
    // the tap that was meant to follow the parent link.
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    this.navParents.forEach((parent) => {
      const toggle = parent.querySelector('[data-nav-toggle]');
      const panel = parent.querySelector('[data-nav-panel]');
      if (!toggle || !panel) return;

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        const open = toggle.getAttribute('aria-expanded') === 'true';
        // One open at a time, so two desktop panels never overlap.
        if (!open) this.closeSubmenus();
        this.setSubmenu(parent, !open);
      });

      // Keyboard users get the same panel without a mouse: tabbing into the
      // parent opens it, tabbing out of it entirely closes it.
      //
      // Gated on :focus-visible, which is the difference between working and
      // not. A pointer press focuses the button BEFORE the click event, so an
      // ungated focusin opened the panel and the click that followed read it as
      // "already open" and closed it again — the chevron did nothing at all on a
      // touch screen. :focus-visible is false for pointer focus and true for
      // keyboard focus, so the two paths no longer fight.
      parent.addEventListener('focusin', (event) => {
        if (!event.target.matches || !event.target.matches(':focus-visible')) return;
        this.closeSubmenus();
        this.setSubmenu(parent, true);
      });
      parent.addEventListener('focusout', (event) => {
        if (!parent.contains(event.relatedTarget)) this.setSubmenu(parent, false);
      });

      // Hover belongs to the desktop nav only. Bound in the mobile drawer too,
      // the pointer entering the row opened the accordion and the tap that
      // followed read as "already open" and closed it again — so tapping the
      // chevron on a laptop trackpad did nothing at all.
      if (!canHover || !parent.closest('.site-header__nav')) return;

      // The close is delayed a little. The pointer can leave the parent's box
      // for a frame on the way to the panel — diagonally past a corner, or over
      // a sub-pixel seam — and closing instantly made the menu feel like it was
      // dodging the cursor.
      parent.addEventListener('mouseenter', () => {
        clearTimeout(parent.__navCloseTimer);
        this.closeSubmenus();
        this.setSubmenu(parent, true);
      });
      parent.addEventListener('mouseleave', () => {
        clearTimeout(parent.__navCloseTimer);
        parent.__navCloseTimer = setTimeout(() => this.setSubmenu(parent, false), 160);
      });
    });

    if (!this.hasSetUpSubmenus) {
      this.hasSetUpSubmenus = true;

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeSubmenus();
      });

      // A click anywhere outside an open parent closes it. Checked against the
      // live node list, so a re-render cannot leave a stale reference open.
      document.addEventListener('click', (event) => {
        const inside = event.target.closest('[data-nav-parent]');
        this.querySelectorAll('[data-nav-parent]').forEach((parent) => {
          if (parent !== inside) this.setSubmenu(parent, false);
        });
      });
    }
  }

  setSubmenu(parent, open) {
    const toggle = parent.querySelector('[data-nav-toggle]');
    const panel = parent.querySelector('[data-nav-panel]');
    if (!toggle || !panel) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.toggleAttribute('hidden', !open);
    parent.toggleAttribute('data-open', open);
  }

  closeSubmenus() {
    this.querySelectorAll('[data-nav-parent]').forEach((parent) => this.setSubmenu(parent, false));
  }

  isMenuOpen() {
    return this.menuPanel?.getAttribute('data-open') === 'true';
  }

  toggleMenu() {
    this.isMenuOpen() ? this.closeMenu() : this.openMenu();
  }

  openMenu() {
    if (!this.menuPanel) return;
    this.menuPanel.setAttribute('data-open', 'true');
    this.menuBackdrop?.setAttribute('data-open', 'true');
    lockScroll(this);
    if (this.releaseFocusTrap) this.releaseFocusTrap();
    this.releaseFocusTrap = trapFocus(this.menuPanel);
  }

  // Release the lock first, and never touch a possibly-missing node before it.
  // `this.menuBackdrop.setAttribute` on a null backdrop threw here, which meant
  // unlockScroll was never reached and the page stayed unscrollable with no
  // menu on screen to close.
  closeMenu() {
    unlockScroll(this);
    this.menuPanel?.setAttribute('data-open', 'false');
    this.menuBackdrop?.setAttribute('data-open', 'false');
    if (this.releaseFocusTrap) {
      this.releaseFocusTrap();
      this.releaseFocusTrap = null;
    }
  }

  // Flags the header once the page has scrolled, which CSS uses to fade in
  // the translucent blurred background. rAF-throttled so the scroll handler
  // never does layout work more than once per frame.
  setupSticky() {
    if (this.header?.dataset.sticky !== 'true') return;

    let ticking = false;
    const apply = () => {
      this.header.classList.toggle('site-header--scrolled', window.scrollY > 8);
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
  }
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}
